import { useCallback, useEffect, useRef, useState } from 'react'

const useHorizontalScroller = ({ step = 240, watchKey, activeSelector = '[data-active="true"]' } = {}) => {
    const scrollContainerRef = useRef(null)
    const [canScrollLeft, setCanScrollLeft] = useState(false)
    const [canScrollRight, setCanScrollRight] = useState(false)

    const updateScrollState = useCallback(() => {
        const scrollElement = scrollContainerRef.current

        if (!scrollElement) {
            return
        }

        setCanScrollLeft(scrollElement.scrollLeft > 0)
        setCanScrollRight(scrollElement.scrollLeft + scrollElement.clientWidth < scrollElement.scrollWidth - 1)
    }, [])

    const scrollByDirection = useCallback((direction) => {
        const scrollElement = scrollContainerRef.current

        if (!scrollElement) {
            return
        }

        scrollElement.scrollBy({
            left: direction * step,
            behavior: 'smooth',
        })
    }, [step])

    const centerActiveItem = useCallback(() => {
        const scrollElement = scrollContainerRef.current
        const activeItem = scrollElement?.querySelector(activeSelector)

        activeItem?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
        })

        updateScrollState()
    }, [activeSelector, updateScrollState])

    useEffect(() => {
        const scrollElement = scrollContainerRef.current

        if (!scrollElement) {
            return undefined
        }

        updateScrollState()
        scrollElement.addEventListener('scroll', updateScrollState)
        window.addEventListener('resize', updateScrollState)

        return () => {
            scrollElement.removeEventListener('scroll', updateScrollState)
            window.removeEventListener('resize', updateScrollState)
        }
    }, [updateScrollState])

    useEffect(() => {
        centerActiveItem()
    }, [watchKey, centerActiveItem])

    return {
        scrollContainerRef,
        canScrollLeft,
        canScrollRight,
        scrollByDirection,
        updateScrollState,
        centerActiveItem,
    }
}

export default useHorizontalScroller